import { useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { IconButton } from '@/ui'
import { useDialogBehaviour } from '@/ui/useDialogBehaviour'

export interface DetailDrawerProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  eyebrow?: ReactNode
  /** Status pills etc. next to the title. */
  meta?: ReactNode
  /** Sticky footer actions. */
  actions?: ReactNode
  children: ReactNode
  width?: 'md' | 'lg'
}

/**
 * Right-hand detail panel over a list (exceptions, payouts). Full width up to tablet, a side panel
 * on desktop; focus is trapped, Escape closes it, the list stays where it was.
 */
export function DetailDrawer({ open, onClose, title, eyebrow, meta, actions, children, width = 'md' }: DetailDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useDialogBehaviour(open, onClose, panelRef)
  if (!open) return null
  return createPortal(
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 animate-fade-in bg-ink/30" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'relative flex h-full w-full animate-fade-in flex-col border-l border-rule bg-paper shadow-pop focus:outline-none',
          width === 'md' ? 'md:max-w-xl' : 'md:max-w-3xl',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-rule bg-surface px-5 py-4">
          <div className="min-w-0">
            {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
            <h2 id={titleId} className="text-[21px] leading-tight text-ink">
              {title}
            </h2>
            {meta && <div className="mt-2 flex flex-wrap items-center gap-1.5">{meta}</div>}
          </div>
          <IconButton label="Close" icon={<X />} onClick={onClose} className="-mr-2 -mt-1" />
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {actions && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-rule bg-surface px-5 py-3">
            {actions}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
}
